// Helpers for narrowing `Message.content` (typed `unknown` at the API
// boundary) and for pairing a `tool_use` message with its later `tool_result`.

import type {
  AssistantTextContent,
  ErrorContent,
  Message,
  MessageOrigin,
  SystemContent,
  ToolResultContent,
  ToolUseContent,
  UserContent,
} from '../types';

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

// Tool names that spawn a subagent (Anthropic `Task`, future `Agent`
// variants). Single home — previously duplicated in ConversationList,
// MessageBubble and aggregate.ts (sprint ai-elements-rendering hoisted it).
export const SUBAGENT_TOOL_NAMES = new Set(['Task', 'Agent']);

// Sprint d028-workflow-agents (D2, m16) — routing set mirroring
// SUBAGENT_TOOL_NAMES's shape. A `Workflow` launch routes to
// `WorkflowPanel` (its N inner agents rendered from the persisted
// `workflow_progress` snapshot, D1) instead of the generic
// MessageBubble/aggregation path, and is excluded from aggregation runs
// (aggregate.ts) the same way SUBAGENT_TOOL_NAMES is. This is a ROUTING
// set, not a verb map — the humanised copy lives in verb-map.ts/
// headline.ts's existing `Workflow: 'Ran a workflow'` entry.
export const WORKFLOW_TOOL_NAMES = new Set(['Workflow']);

// Sprint stdout-transport-replay (D013-EC-118) — system subtypes that
// signal LOST OR INCOMPLETE agent output. They never render as calm muted
// pills, and (sprint turn-collapse-final-answer, D4) they never render
// inside a collapsed turn either. Hoisted here from SystemPill.tsx when
// the turn-collapse pass needed the same set: one home, two consumers.
// Sprint context-limit-survivable (D-C2 / D013-EC-191a) — `'error'` joins
// the set. It is precisely the class the set exists for: `subtype:'error'`
// is what `failFailoverResume` writes when a conversation can no longer be
// resumed, including the "usa Fork" instruction that is the founder's only
// way out. It had NO renderer at all, so it rendered as nothing.
//
// TWO consumers, not one — check both before touching this set:
//   • SystemPill.tsx:7        — loud styling instead of the calm muted pill.
//   • turn-boundaries.ts      — `isLoudPill` → `splitWorkSegments` (sprint
//                               work-segment-collapse; formerly
//                               `splitTurnItems`, retired), where a loud
//                               pill BREAKS a work segment and keeps its
//                               chronological position OUTSIDE every panel
//                               (D6(a)). Adding a subtype here changes that
//                               behaviour too; desired here (an
//                               unrecoverable stop must not be hidden
//                               behind a chevron) and asserted, not assumed.
export const LOUD_SYSTEM_SUBTYPES = new Set([
  'transport_gap',
  'output_truncated',
  'error',
]);

// Sprint context-limit-survivable (D-C3 / D013-EC-191b) — ONE home for the
// model-badge shortener; it existed verbatim twice (AssistantMessage.tsx,
// SystemPill.tsx).
//
// Returns `null` — not the input — on any string that is not a recognised
// Claude model id. The old fallback returned its argument unchanged, so the
// literal `<synthetic>` (the model tag the backend's oversize detector
// depends on, `session-manager.ts:5052`, and therefore honest data we keep
// persisting) rendered verbatim as a founder-visible badge. Fixing the
// RENDERER instead of the data is the class fix: every non-model string is
// covered, now and later. Callers skip the badge on `null`.
export function shortenModel(model: string): string | null {
  const m = model.match(/^claude-([a-z]+)-(\d+)-(\d+)/);
  if (!m) return null;
  return `${m[1]} ${m[2]}.${m[3]}`;
}

export function asAssistantText(m: Message): AssistantTextContent {
  if (isObj(m.content) && typeof m.content.text === 'string') {
    const out: AssistantTextContent = { text: m.content.text };
    if (typeof m.content.model === 'string') out.model = m.content.model;
    return out;
  }
  return { text: '' };
}

export function asUserContent(m: Message): UserContent {
  if (isObj(m.content) && typeof m.content.text === 'string') {
    return { text: m.content.text };
  }
  return { text: '' };
}

export function asToolUse(m: Message): ToolUseContent {
  if (isObj(m.content)) {
    const id =
      typeof m.content.tool_use_id === 'string' ? m.content.tool_use_id : '';
    const name =
      typeof m.content.name === 'string' ? m.content.name : 'unknown';
    const input = isObj(m.content.input) ? m.content.input : {};
    return { tool_use_id: id, name, input };
  }
  return { tool_use_id: '', name: 'unknown', input: {} };
}

export function asToolResult(m: Message): ToolResultContent {
  if (isObj(m.content)) {
    const id =
      typeof m.content.tool_use_id === 'string' ? m.content.tool_use_id : '';
    return {
      tool_use_id: id,
      content: m.content.content,
      is_error: m.content.is_error === true,
    };
  }
  return { tool_use_id: '', content: null, is_error: false };
}

export function asSystem(m: Message): SystemContent {
  if (isObj(m.content) && typeof m.content.subtype === 'string') {
    return m.content as SystemContent;
  }
  return { subtype: 'unknown' };
}

export function asError(m: Message): ErrorContent {
  if (isObj(m.content)) {
    const out: ErrorContent = {};
    if (typeof m.content.reason === 'string') out.reason = m.content.reason;
    if ('detail' in m.content) out.detail = m.content.detail;
    return out;
  }
  return {};
}

// Stringify a tool_result's content for display. Backend can give us a
// string, an array of blocks, or arbitrary JSON. Anything is safer than
// rendering [object Object].
export function toolResultText(content: unknown): string {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((b) => {
        if (isObj(b) && typeof b.text === 'string') return b.text;
        return JSON.stringify(b, null, 2);
      })
      .join('\n');
  }
  try {
    return JSON.stringify(content, null, 2);
  } catch {
    return String(content);
  }
}

// Build a Map of tool_use_id → matching tool_result message, given the
// agent's full ordered message list. We pair on the explicit id when both
// sides expose one (the common case); otherwise we leave the result
// unpaired and the renderer will show it as a stand-alone block.
export function pairToolResults(messages: Message[]): {
  resultByToolUseId: Map<string, Message>;
  pairedResultIds: Set<string>;
} {
  const resultByToolUseId = new Map<string, Message>();
  const pairedResultIds = new Set<string>();

  for (const m of messages) {
    if (m.type !== 'tool_result') continue;
    const tr = asToolResult(m);
    if (!tr.tool_use_id) continue;
    if (!resultByToolUseId.has(tr.tool_use_id)) {
      resultByToolUseId.set(tr.tool_use_id, m);
      pairedResultIds.add(m.id);
    }
  }

  return { resultByToolUseId, pairedResultIds };
}

// Sprint subagent-launch-ack-liveness (D3, D013-EC-659) — empareja el LANZAMIENTO de un
// subagente en segundo plano con su FIN REAL: el mensaje `system` `task_notification` que el
// backend persiste (D1). Gemelo de `pairToolResults`, y por la misma razón: el render no debe
// recorrer la historia por cada tarjeta.
//
// SOBRE TODOS LOS MENSAJES, no sobre `topLevel` (EC-a): el fin se persiste en la RAÍZ por los
// dos caminos, así que un nieto a profundidad N sólo lo encuentra si el mapa se calculó sobre
// la lista completa.
//
// EL PRIMERO GANA, igual que `pairToolResults`: el residual de D2 se resuelve
// determinísticamente por orden de stream. Sesgo conocido y NO medido: cuando el orden es
// texto-primero, el payload que gana es el pobre (`duration_ms: null`, summary genérico) —
// FM10, con su señal de detección.
export function pairTaskNotifications(messages: Message[]): Map<string, Message> {
  const byToolUseId = new Map<string, Message>();
  for (const m of messages) {
    if (m.type !== 'system') continue;
    const c = m.content as { subtype?: unknown; tool_use_id?: unknown } | null;
    if (c?.subtype !== 'task_notification') continue;
    const id = typeof c.tool_use_id === 'string' ? c.tool_use_id : '';
    if (id === '' || byToolUseId.has(id)) continue;
    byToolUseId.set(id, m);
  }
  return byToolUseId;
}

// D3 / B2 — EL ÚNICO predicado de "este `tool_use` es un lanzamiento de subagente en segundo
// plano". EL NOMBRE DE HERRAMIENTA ES PARTE DEL PREDICADO, no un adorno: el backend lee
// `run_in_background` sin filtrar por herramienta (`session-manager.ts:8039`), así que un
// `Bash` en segundo plano TAMBIÉN abre `kind:'subagent'` con el flag puesto — y su recibo dice
// "Command running in background" y NUNCA le sigue un `task_notification`. Sin este conjunto,
// la rama de `itemEndMs` de D6 devolvería `null` para siempre y el segmento se quedaría `live`
// eternamente ("Trabajando desde …" sobre un `sleep` que acabó hace semanas).
// `SUBAGENT_TOOL_NAMES` (`:22`) es el dueño del conjunto {Task, Agent}; no se duplica aquí.
export function isBackgroundSubagentLaunch(m: Message): boolean {
  if (m.type !== 'tool_use') return false;
  const tu = asToolUse(m);
  return SUBAGENT_TOOL_NAMES.has(tu.name) && tu.input.run_in_background === true;
}

// EC-e — el ÚNICO sitio donde se decide si el `tool_result` de un lanzamiento es su fin.
// `is_error` gana sobre `run_in_background`: un lanzamiento en segundo plano que FALLÓ al
// arrancar nunca recibirá un `task_notification`, así que ese `tool_result` sí es el fin.
export function ackIsRealEnd(resultMessage: Message | undefined): boolean {
  if (!resultMessage) return false;
  return (resultMessage.content as { is_error?: unknown } | null)?.is_error === true;
}

/**
 * D3/D4/D6 — TODO lo que hace falta para decidir el estado de un lanzamiento en segundo
 * plano. UN tipo, DOS consumidores (`SubagentPanel`, `turn-boundaries.ts`), cero duplicación.
 *
 * NO extiende `RowLivenessCtx` (`founder-layer.ts:44-47`) a propósito: su lector
 * `isPlausiblyLive` empaqueta `turnClosedAfter`, y esa mitad NO aplica a un hijo en segundo
 * plano — sobrevive legítimamente al cierre de su turno (D013-EC-137, §7). La mitad que sí
 * aplica (`agentWorking`) viaja aquí, leída de la MISMA fuente que alimenta `ctxForMessage`
 * (`ConversationList.tsx:164`, `agent?.working ?? false`, definida por `cliWorkInFlight`,
 * `session-manager.ts:6089`).
 */
export interface BackgroundLaunchCtx {
  /** `tool_use_id` → mensaje `task_notification` del fin real. */
  completionByToolUseId: ReadonlyMap<string, Message>;
  /**
   * B3 — COTA DE EDAD. Ids de MENSAJE de lanzamiento con MÁS DE UN cierre de turno
   * (`TURN_CLOSING_SUBTYPES`: `result`/`exit`, NUNCA `turn_boundary`) después en el stream.
   * Uno se permite porque un hijo en segundo plano sobrevive al cierre de SU turno por diseño
   * (D013-EC-137); dos significa que sobrevivió también al turno siguiente completo, y ahí la
   * verdad disponible es "sin confirmación de fin", no "trabajando". Sin esta cota, todo
   * lanzamiento en segundo plano del historial (semanas) renderiza `running` con Loader en
   * cuanto el agente trabaje en cualquier cosa, porque `ctxForMessage` devuelve el ÚNICO
   * `agentWorking` actual para todo mensaje sin importar su edad. Cierra además el 26 % de
   * D013-EC-169 que nunca notifica.
   */
  staleLaunchIds: ReadonlySet<string>;
  /**
   * C13 — el padre está bloqueado en una tarjeta de permiso / ask_user_question sin resolver.
   * Entonces `agentWorking === false` NO significa "paró": `rollup.ts:522` corta a `needs-you`
   * y `own` excluye `subagent` (`:533`), así que `cliWorkInFlight` (`session-manager.ts:6089`)
   * es `false` con el hijo VIVO. Sin este campo, D4 pintaría `unconfirmed` sobre un hijo vivo
   * exactamente cuando el founder está mirando la tarjeta que lo bloquea.
   */
  parentBlockedOnCard: boolean;
  /** `agent.working` del PADRE. La mitad aplicable de `RowLivenessCtx`, misma fuente. */
  agentWorking: boolean;
}

// Sprint 5.6 (D9) — group child messages under their parent Agent/Task
// tool_use. Anthropic stamps every event the subagent produces with
// parent_tool_use_id pointing to the parent. Returns:
//   • topLevel: messages where parentToolUseId is null/undefined (rendered
//              in the main timeline).
//   • childrenByParent: parent_tool_use_id → child messages (rendered
//              inside the SubagentPanel; nested subagents recurse on the
//              same map — sprint ai-elements-rendering).
//   • orphanNoticeIds: first message id of each orphan group. An orphan
//              carries a parent_tool_use_id whose tool_use is not in the
//              history (pruned/truncated). Pre-sprint these silently never
//              rendered; now they re-home to topLevel and the first one per
//              missing parent gets a dev-only notice (spec §7).
//   • orphanIds: EVERY orphan's message id, not just the first per group.
//              Campaña agent-noise-collapse (resultado 2) — a re-homed
//              orphan is the real source of the raw walls Santi saw: with
//              37 background launches in 4 h the parent `tool_use` rows
//              fall outside `listAgentInitialWindow` (min 200 / cap 1000),
//              so the children land at root and render as bare bubbles.
//              Founder mode needs the full set to collapse them; the
//              existing `orphanNoticeIds` is a strict subset and stays
//              exactly as it was (the dev-only notice is still one per
//              missing parent, not one per row).
export function groupBySubagent(messages: Message[]): {
  topLevel: Message[];
  childrenByParent: Map<string, Message[]>;
  orphanNoticeIds: Set<string>;
  orphanIds: Set<string>;
} {
  // Pass 1 — ids of every tool_use present. During live streaming the
  // parent Task tool_use always lands before its children, so a missing
  // parent means genuinely pruned history, not an ordering race.
  const knownToolUseIds = new Set<string>();
  for (const m of messages) {
    if (m.type !== 'tool_use') continue;
    const id = asToolUse(m).tool_use_id;
    if (id) knownToolUseIds.add(id);
  }

  const topLevel: Message[] = [];
  const childrenByParent = new Map<string, Message[]>();
  const orphanNoticeIds = new Set<string>();
  const orphanIds = new Set<string>();
  const noticedParents = new Set<string>();
  for (const m of messages) {
    const parent = m.parentToolUseId ?? null;
    if (!parent) {
      topLevel.push(m);
      continue;
    }
    if (!knownToolUseIds.has(parent)) {
      topLevel.push(m);
      orphanIds.add(m.id);
      if (!noticedParents.has(parent)) {
        noticedParents.add(parent);
        orphanNoticeIds.add(m.id);
      }
      continue;
    }
    const arr = childrenByParent.get(parent) ?? [];
    arr.push(m);
    childrenByParent.set(parent, arr);
  }
  return { topLevel, childrenByParent, orphanNoticeIds, orphanIds };
}

// Sprint agent-message-attribution (D6) — strip the reserved-grammar
// control frame (`[fleet-shepherd …]`, `[fleet-ask …]`, `[fleet-dispatch
// …]`) from the founder-visible body of a non-founder message. Storage is
// NEVER touched — this is a render-time split, mirroring the backend's
// `sanitizeReservedGrammar` (session-manager.ts:699-714) escape without
// undoing it: the escaped forms (`\[fleet-ask …]`, `\[fleet-shepherd
// …]`, written by the sanitizer for /ask and /send) and the unescaped
// form (`[fleet-dispatch <id>]`, written straight by `spawnAndWire`'s
// firstPrompt template — it never passes through `sendMessage`, so the
// sanitizer never touches it) are BOTH matched, backslash optional.
//
// Splits ONLY when origin is non-founder (truthy, not 'founder') AND
// line 1 of `text` — the WHOLE line, anchored both ends — matches the
// grammar. A founder-typed lookalike (origin 'founder') is the
// principal's own text and is never rewritten (D6). A frame-shaped line
// anywhere other than line 1 is body, not a control frame (the real
// frame is always written first).
const FLEET_FRAME_LINE = /^\\?\[fleet-(shepherd|ask|dispatch)[^\]]*\]$/;

export function splitFleetFrame(
  text: string,
  origin: MessageOrigin | null | undefined,
): { frame: string | null; body: string } {
  if (!origin || origin === 'founder') return { frame: null, body: text };
  const newlineIdx = text.indexOf('\n');
  const firstLine = newlineIdx === -1 ? text : text.slice(0, newlineIdx);
  if (!FLEET_FRAME_LINE.test(firstLine)) return { frame: null, body: text };
  const body = newlineIdx === -1 ? '' : text.slice(newlineIdx + 1);
  return { frame: firstLine, body };
}

// Sprint chatgpt-message-rail (D1) — single owner of "did Santi write
// this". Moved here from `UserMessage.tsx` (formerly a local, unexported
// function, `origin === 'founder' || origin == null`; D2 of
// `chatgpt-user-bubble`: `null` = a pre-055 row, mostly Santi's). Any
// other origin (`wakeup` / `system` / `fleet:*` / unknown string) is not
// founder-authored.
export function isFounderOrigin(origin: MessageOrigin | null | undefined): boolean {
  return origin === 'founder' || origin === null || origin === undefined;
}

// Sprint machine-message-collapse (D1) — single owner of "does this
// machine-authored row collapse to a divider marker instead of a bubble".
// Closed list, founder-decided 2026-09-12: `system` and `wakeup` only.
// `fleet:*` is deliberately NOT here ("por ahora no plegaría los mensajes
// que vienen de otros agentes"); widening it later is one line here, and
// nowhere else. Founder / null / undefined / unknown → false (fail-closed
// to today's bubble, same philosophy as `MessageOriginChip` D3). Type
// predicate so the dispatcher can pass the narrowed origin straight to
// `CollapsedMachineMessage` without a cast.
export function isCollapsedMachineOrigin(
  origin: MessageOrigin | null | undefined,
): origin is 'system' | 'wakeup' {
  return origin === 'system' || origin === 'wakeup';
}

// Sprint 5.6 (D25) — count distinct models used across assistant turns.
// The per-turn model badge is gated on this set having ≥2 entries (the
// user explicitly switched at least once).
export function distinctModelsInHistory(messages: Message[]): Set<string> {
  const set = new Set<string>();
  for (const m of messages) {
    if (m.type !== 'assistant_text') continue;
    const c = asAssistantText(m);
    if (c.model) set.add(c.model);
  }
  return set;
}

